import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import * as HSVHelpers from '../../helpers/hsv-helpers';
import { HueComponent } from '../hue-selector/hue-selector.component';
import { SaturationValueComponent } from '../saturation-value-selector/saturation-value.component';
import { ColorToolbarComponent } from '../toolbar/toolbar.component';

@Component({
  selector: 'mode-color-picker-container',
  standalone: true,
  imports: [CommonModule, ColorToolbarComponent, SaturationValueComponent, HueComponent],
  templateUrl: './color-picker-container.component.html',
  styleUrls: ['./color-picker-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerContainerComponent implements OnChanges, OnDestroy {
  @Input() currentColor = '';
  @Input() savedColor = '';
  @Input() updateOnMove = true;
  @Output() colorUpdated = new EventEmitter<string>();

  currentHue = 0;
  currentSaturation = 0.5;
  currentValue = 0.5;
  isUpdating = false;
  lastColorEmitted = '';

  colorUpdateSubject$ = new Subject<string>();
  destroy$ = new Subject<void>();

  constructor() {
    this.colorUpdateSubject$
      .pipe(throttleTime(100, undefined, { leading: true, trailing: true }), takeUntil(this.destroy$))
      .subscribe((color) => this.colorUpdated.emit(color));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentColor'] && !this.isUpdating) {
      this.generateHsvColor(this.currentColor);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.colorUpdateSubject$.complete();
  }

  generateHsvColor(color: string) {
    const hsvColor = HSVHelpers.getHSV(color);
    // Update saturation, value, and hue
    this.currentHue = Number.isNaN(hsvColor.h) ? this.currentHue : hsvColor.h;
    this.currentSaturation = hsvColor.s;
    this.currentValue = hsvColor.v;
  }

  onToolbarColorUpdated(color: string) {
    this.currentColor = this.formatColor(color);
    this.generateHsvColor(color);
  }

  onSaturationColorUpdated(values: { s: number; v: number }) {
    const color = HSVHelpers.hsvAsHex(this.currentHue, values.s, values.v);
    // Update saturation and value only
    this.currentSaturation = values.s;
    this.currentValue = values.v;
    this.currentColor = this.formatColor(color);
    if (this.updateOnMove || (!this.updateOnMove && !this.isUpdating)) {
      this.colorUpdateSubject$.next(color);
    }
  }

  onHueChange(hue: number) {
    this.currentHue = hue;
  }

  onMoveStart() {
    this.isUpdating = true;
  }

  formatColor(color: string) {
    return color.replace('#', '').toUpperCase();
  }

  onMoveEnd() {
    this.isUpdating = false;
    // Need to force the color update because we won't get an update
    // through onSaturationColorUpdated if the hue doesn't explicitly
    // change on mouse up.
    const formattedColor = this.formatColor(this.currentColor);
    if (this.lastColorEmitted !== formattedColor) {
      this.lastColorEmitted = formattedColor;
      this.colorUpdated.emit('#' + formattedColor);
    }
  }
}
